import React, { useState, useEffect, useCallback } from 'react';
import logo from './vdc_logo.png';
import { useNavigate } from 'react-router-dom';
import { BASE_URL } from "./Constants.js";
import useAuth from './useAuth';

const INACTIVITY_TIMEOUT = 15 * 60 * 1000;

const Header = ({ onToggleSidebar, isSidebarOpen }) => {
  useAuth();

  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Stock validation states
  const [stockStatus, setStockStatus] = useState(null);
  const [stockLoading, setStockLoading] = useState(true);

  const navigate = useNavigate();

  /*
   * LOGOUT
   */
  const handleLogout = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');

      if (token) {
        await fetch(`${BASE_URL}logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      }

    } catch (error) {
      console.error('Error logging out:', error);

    } finally {
      localStorage.removeItem('authToken');
      localStorage.removeItem('exp');

      sessionStorage.removeItem('user');
      sessionStorage.removeItem('userProfile');
      sessionStorage.removeItem('userEmpCode');

      navigate('/');
    }
  }, [navigate]);


  /*
   * FETCH USER PROFILE
   */
  useEffect(() => {
    const token = localStorage.getItem('authToken');

    const fetchProfile = async () => {
      try {
        const response = await fetch(`${BASE_URL}profile`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch profile');
        }

        const data = await response.json();

        setUser(data);

      } catch (error) {
        setError(error.message);
        console.error('Error fetching profile:', error);

      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchProfile();
    } else {
      setLoading(false);
      navigate('/');
    }

  }, [navigate]);


  /*
   * FETCH STOCK VALIDATION STATUS
   */
  useEffect(() => {

    const fetchStockValidationStatus = async () => {

      const token = localStorage.getItem('authToken');

      if (!token) {
        setStockLoading(false);
        return;
      }

      try {

        const response = await fetch(
          `${BASE_URL}stock_validation_status`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch stock validation status');
        }

        const result = await response.json();

        console.log("Stock Validation Response:", result);

        setStockStatus(result);

      } catch (error) {

        console.error(
          'Error fetching stock validation status:',
          error
        );

      } finally {

        setStockLoading(false);

      }
    };

    fetchStockValidationStatus();

  }, []);


  /*
   * AUTO LOGOUT WHEN USER LEAVES OUR TAB
   */
  useEffect(() => {

    let logoutTimer = null;

    const startLogoutTimer = () => {

      console.log(
        "User left application. Starting 15 minute timer."
      );

      clearTimeout(logoutTimer);

      logoutTimer = setTimeout(() => {

        console.log(
          "15 minutes passed while application was inactive."
        );

        handleLogout();

      }, INACTIVITY_TIMEOUT);

    };


    const cancelLogoutTimer = () => {

      console.log(
        "User returned to application."
      );

      clearTimeout(logoutTimer);

      logoutTimer = null;

    };


    const handleVisibilityChange = () => {

      if (document.visibilityState === "hidden") {

        startLogoutTimer();

      } else {

        cancelLogoutTimer();

      }

    };


    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange
    );


    return () => {

      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );

      clearTimeout(logoutTimer);

    };

  }, [handleLogout]);


  const toggleAccountDropdown = (e) => {

    e.preventDefault();

    setIsAccountDropdownOpen(
      prevState => !prevState
    );

  };


  if (loading) {
    return <div>Loading...</div>;
  }


  if (error) {
    return <div>Error: {error}</div>;
  }


  /*
   * STOCK STATUS TOOLTIP CONTENT
   */
  const getStockTooltip = () => {

    if (!stockStatus) {
      return "Stock validation status unavailable";
    }

    // GREEN
    if (stockStatus.variance_status === "GREEN") {

      return `Variance Count: ${stockStatus.variance_count || 0}`;

    }

    // RED
    if (
      stockStatus.variance_status === "RED" &&
      stockStatus.data &&
      stockStatus.data.length > 0
    ) {

      return stockStatus.data
        .map((item, index) => {

          return `
Record ${index + 1}
Area: ${item.area_name}
Item: ${item.uniform_type}
Size: ${item.uniform_size}
Color: ${item.uniform_color}
Gender: ${item.gender}
GRN Quantity: ${item.grn_quantity}
Delivered Qty: ${item.delivered_qty}
Pending Qty: ${item.pending_qty}
Physical Stock: ${item.physical_stock}
Remaining Stock: ${item.remaining_stock}
System Stock: ${item.sys_stock}
Variance: ${item.variance}
          `.trim();

        })
        .join('\n\n');

    }

    return "Variance found";

  };


  return (
    <>
      <header
        className="header-desktop"
        style={{
          background: '#fff',
          zIndex: 531,
          width: '100%',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: '80px',
          boxShadow: '0px 1px 4px rgba(0,0,0,0.1)',
        }}
      >

        <div
          className="section__content section__content--p30"
          style={{
            paddingLeft: '0px',
            transition: 'padding-left 0.3s ease',
          }}
        >

          <div className="container-fluid">

            <div className="header-wrap">

              <button
                className="btn btn-outline-secondary d-none d-lg-inline-block me-3"
                style={{
                  marginLeft:
                    isSidebarOpen && window.innerWidth >= 992
                      ? '300px'
                      : '100px',

                  transition: 'margin-left 0.3s ease',
                }}
                onClick={onToggleSidebar}
              >
                ☰
              </button>


              <form
                className="form-header"
                action=""
                method="POST"
              />


              <div className="header-button">

                <div className="noti-wrap">
                  {/* Notification section */}
                </div>


                {/* =========================================
                    STOCK VALIDATION INDICATOR
                ========================================= */}

                {!stockLoading && stockStatus && (

                  <div
                    className="stock-validation-indicator"
                    title={getStockTooltip()}
                  >

                    {stockStatus.variance_status === "GREEN" ? (

                      <div
                        className="stock-green"
                        title={getStockTooltip()}
                      >
                        ✓
                      </div>

                    ) : (

                      <div
                        className="stock-red"
                        title={getStockTooltip()}
                      >
                        ✕
                      </div>

                    )}

                  </div>

                )}


                <div className="account-wrap">

                  <div className="account-item clearfix js-item-menu">

                    <div className="image">

                      <img
                        src={logo}
                        alt="User"
                      />

                    </div>


                    <div className="content">

                      {user &&
                      user.profile &&
                      user.profile.emp_code ? (

                        <div>

                          <span className="user-id">
                            {user.profile.emp_code}
                          </span>

                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              handleLogout();
                            }}
                          >
                            <i className="zmdi zmdi-power"></i>
                            Logout
                          </a>

                        </div>

                      ) : (

                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handleLogout();
                          }}
                        >
                          <i className="zmdi zmdi-power"></i>
                          Logout
                        </a>

                      )}

                    </div>


                    <div
                      className={`account-dropdown js-dropdown ${
                        isAccountDropdownOpen ? 'show' : ''
                      }`}
                    >

                      <div className="info clearfix">

                        <div className="image">

                          <a href="#">
                            <img
                              src="/images/logo.jpg"
                              alt="VDC"
                            />
                          </a>

                        </div>


                        <div className="content">

                          <h5 className="name">

                            <a href="#">
                              {user?.profile?.emp_code || "User"}
                            </a>

                          </h5>

                        </div>

                      </div>


                      <div className="account-dropdown__footer">

                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handleLogout();
                          }}
                        >
                          <i className="zmdi zmdi-power"></i>
                          Logout
                        </a>

                      </div>

                    </div>

                  </div>

                </div>

              </div>

            </div>

          </div>

        </div>

      </header>


      {/* =========================================
          STOCK VALIDATION CSS
      ========================================= */}

      <style>{`

        .stock-validation-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 20px;
          cursor: pointer;
        }

        .stock-green,
        .stock-red {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 21px;
          font-weight: bold;
          color: white;
          cursor: pointer;
          transition: transform 0.2s ease;
        }

        .stock-green {
          background-color: #16a34a;
          box-shadow: 0 2px 6px rgba(22, 163, 74, 0.35);
        }

        .stock-red {
          background-color: #dc2626;
          box-shadow: 0 2px 6px rgba(220, 38, 38, 0.35);
        }

        .stock-green:hover,
        .stock-red:hover {
          transform: scale(1.12);
        }

      `}</style>

    </>
  );
};

export default Header;
